// src/components/PlanManagementScreen.jsx
// Gestão do Plano — billing seguro.
//
// Regra principal:
// - Upgrade para Premium só é aplicado localmente depois do backend confirmar na Stripe.
// - Downgrade Premium -> Essencial fica agendado para o fim do ciclo pago.
// - Enquanto downgrade está agendado, usuário continua Premium até downgradeEfetivoEm.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CreditCard,
  Gem,
  Menu,
  Sparkles,
  Sprout,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/plan-management.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
  PLAN: "plano",
  PLAN_VALUE: "valorPlano",
  STATUS_ASSINATURA: "statusAssinatura",
  ASSINATURA_ATIVA: "assinaturaAtiva",
  AVISO_ASSINATURA: "avisoAssinatura",
  RECURSOS: "recursos",
  PLANO_AGENDADO: "planoAgendado",
  VALOR_PLANO_AGENDADO: "valorPlanoAgendado",
  DOWNGRADE_AGENDADO: "downgradeAgendado",
  DOWNGRADE_EFETIVO_EM: "downgradeEfetivoEm",
  DOWNGRADE_SOLICITADO_EM: "downgradeSolicitadoEm",
  DOWNGRADE_SCHEDULE_ID: "downgradeScheduleId",
};

/*
 * Price IDs oficiais da Stripe.
 *
 * Essencial: R$ 29,97/mês
 * Premium: R$ 69,97/mês
 *
 * O backend deve validar esses IDs antes de alterar qualquer assinatura.
 */
const PLAN_PRICE_IDS = {
  essencial:
    process.env.REACT_APP_STRIPE_PRICE_ESSENCIAL ||
    "price_1U18NBKKOfm7LsUJzf5rztCc",

  premium:
    process.env.REACT_APP_STRIPE_PRICE_PREMIUM ||
    "price_1U18OVKKOfm7LsUJnbIcjwgc",
};

const PLAN_META = {
  essencial: {
    id: "essencial",
    shortName: "Essencial",
    name: "PsyAssist Essencial",
    price: "29,97",
    icon: Sprout,
    tone: "essential",
    description: "Recursos principais para organizar atendimentos, pacientes e rotina.",
    features: [
      "Cadastro de pacientes",
      "Prontuários e anamnese",
      "Agendamentos e controle de sessões",
      "Financeiro: pagamentos e recebimentos",
      "Gestão de leads",
      "Tela de pagamentos não realizados",
    ],
  },
  premium: {
    id: "premium",
    shortName: "Premium",
    name: "PsyAssist Premium",
    price: "69,97",
    icon: Gem,
    tone: "premium",
    description: "Gestão avançada com relatórios, IA e automações.",
    features: [
      "Todos os recursos do Essencial",
      "Agente PsyAssist com insights financeiros e clínicos",
      "Integração com Google Agenda",
      "Integração com Google Meet",
      "Régua de cobrança automática configurável por e-mail",
      "Lembretes de sessão configuráveis por e-mail",
      "WhatsApp em implementação",
    ],
  },
};

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function writeStorage(key, value) {
  try {
    if (value === undefined || value === null || value === "") {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
      return;
    }

    sessionStorage.setItem(key, String(value));
    localStorage.setItem(key, String(value));
  } catch {
    // storage indisponível
  }
}

function writeJsonStorage(key, value) {
  writeStorage(key, JSON.stringify(value || {}));
}

function clearAuthStorage() {
  [
    "psy_token",
    "psy_id",
    "psy_usuario",
    "psy_email",
    "psy_nome",
    "plano",
    "valorPlano",
    "statusAssinatura",
    "assinaturaAtiva",
    "avisoAssinatura",
    "recursos",
    "planoAgendado",
    "valorPlanoAgendado",
    "downgradeAgendado",
    "downgradeEfetivoEm",
    "downgradeSolicitadoEm",
    "downgradeScheduleId",
    "token",
    "id",
    "user",
    "usuario",
    "email",
  ].forEach((key) => {
    try {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } catch {
      // storage indisponível
    }
  });
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();
  if (!clean) return "";
  return clean.split(/\s+/)[0];
}

function normalizePlanId(value = "") {
  const clean = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (clean.includes("premium") || clean.includes("pro")) return "premium";
  return "essencial";
}

function normalizePrice(value = "", fallback = "29,97") {
  const clean = String(value || "").trim();

  if (!clean) return fallback;

  return clean
    .replace("R$", "")
    .replace("/MÊS", "")
    .replace("/Mês", "")
    .replace("/mês", "")
    .replace("/MES", "")
    .replace("/mes", "")
    .trim();
}

function formatPrice(value = "") {
  return `R$ ${normalizePrice(value)}/MÊS`;
}

function formatDateBR(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function buildRecursosForPlan(planValue) {
  const isPremium = normalizePlanId(planValue) === "premium";

  return {
    ia: isPremium,
    insightsIa: isPremium,
    cobrancas: isPremium,
    relatoriosAvancados: isPremium,
    googleAgenda: false,
  };
}

function buildAuthHeaders() {
  const token = readStorage([AUTH_KEYS.TOKEN, "token"]);

  if (!token) {
    return { "Content-Type": "application/json" };
  }

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.detail || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function PlanFeatureList({ features = [] }) {
  return (
    <ul className="pm-features">
      {features.map((feature) => (
        <li key={feature}>{feature}</li>
      ))}
    </ul>
  );
}

function PlanOption({ plan, active, next }) {
  const Icon = plan.icon;

  return (
    <article className={`pm-option pm-option--${plan.tone} ${active ? "is-active" : ""}`}>
      <div className="pm-option__top">
        <div className="pm-option__icon" aria-hidden="true">
          <Icon size={20} />
        </div>

        <div className="pm-option__title">
          <strong>{plan.shortName}</strong>
          <span>R$ {plan.price}/MÊS</span>
        </div>

        <div className="pm-option__badge">
          {active ? "Atual" : next ? "Próximo" : "Disponível"}
        </div>
      </div>

      <p>{plan.description}</p>
    </article>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="pm-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="pm-dialog" onClick={(event) => event.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export default function PlanManagementScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [planId, setPlanId] = useState("essencial");
  const [planName, setPlanName] = useState(PLAN_META.essencial.name);
  const [planPrice, setPlanPrice] = useState(formatPrice(PLAN_META.essencial.price));

  const [downgradeAgendado, setDowngradeAgendado] = useState(false);
  const [planoAgendado, setPlanoAgendado] = useState("");
  const [valorPlanoAgendado, setValorPlanoAgendado] = useState("");
  const [downgradeEfetivoEm, setDowngradeEfetivoEm] = useState("");

  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "Usuário", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);
  const userId = useMemo(() => readStorage([AUTH_KEYS.USER_ID, "id"]), []);

  useEffect(() => {
    const storedPlan = readStorage([AUTH_KEYS.PLAN]) || "essencial";
    const storedValue = readStorage([AUTH_KEYS.PLAN_VALUE]);
    const storedDowngrade = readStorage([AUTH_KEYS.DOWNGRADE_AGENDADO]) === "true";
    const storedPlanoAgendado = readStorage([AUTH_KEYS.PLANO_AGENDADO]);
    const storedValorPlanoAgendado = readStorage([AUTH_KEYS.VALOR_PLANO_AGENDADO]);
    const storedDowngradeEfetivoEm = readStorage([AUTH_KEYS.DOWNGRADE_EFETIVO_EM]);

    const currentPlanId = normalizePlanId(storedPlan);
    const currentPlan = PLAN_META[currentPlanId] || PLAN_META.essencial;
    const currentPrice = normalizePrice(storedValue, currentPlan.price);

    setPlanId(currentPlanId);
    setPlanName(currentPlan.name);
    setPlanPrice(formatPrice(currentPrice));
    setDowngradeAgendado(storedDowngrade);
    setPlanoAgendado(storedPlanoAgendado);
    setValorPlanoAgendado(storedValorPlanoAgendado);
    setDowngradeEfetivoEm(storedDowngradeEfetivoEm);
  }, []);

  useEffect(() => {
    if (!dialog) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape" && !busy) {
        setDialog(null);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dialog, busy]);

  const currentPlan = PLAN_META[planId] || PLAN_META.essencial;
  const isPremium = planId === "premium";
  const CurrentPlanIcon = currentPlan.icon;

  const nextPlan = useMemo(() => {
    const nextPlanId = downgradeAgendado ? "premium" : isPremium ? "essencial" : "premium";
    const meta = PLAN_META[nextPlanId];

    return {
      ...meta,
      newPlanId: nextPlanId,
      newPlanName: meta.name,
      newPlanPrice: formatPrice(meta.price),
      newPriceOnlyNumber: meta.price,
      newPriceId: PLAN_PRICE_IDS[nextPlanId],
    };
  }, [downgradeAgendado, isPremium]);

  const NextPlanIcon = nextPlan.icon;

  const actionLabel = downgradeAgendado
    ? "Manter Premium"
    : isPremium
      ? "Agendar downgrade para Essencial"
      : "Mudar para Premium";

  function closeDialog() {
    if (busy) return;
    setDialog(null);
  }

  function applyServerPlan(serverData = {}) {
    const effectivePlan = serverData?.plano || planName || PLAN_META.essencial.name;
    const effectivePlanId = normalizePlanId(effectivePlan);
    const effectiveMeta = PLAN_META[effectivePlanId] || PLAN_META.essencial;
    const effectivePrice = normalizePrice(serverData?.valorPlano, effectiveMeta.price);
    const recursos = serverData?.recursos || buildRecursosForPlan(effectivePlan);

    const nextDowngradeAgendado = Boolean(serverData?.downgradeAgendado);
    const nextPlanoAgendado = serverData?.planoAgendado || "";
    const nextValorPlanoAgendado = serverData?.valorPlanoAgendado || "";
    const nextDowngradeEfetivoEm = serverData?.downgradeEfetivoEm || "";

    writeStorage(AUTH_KEYS.PLAN, effectivePlan);
    writeStorage(AUTH_KEYS.PLAN_VALUE, effectivePrice);
    writeStorage(AUTH_KEYS.STATUS_ASSINATURA, serverData?.statusAssinatura || "active");
    writeStorage(AUTH_KEYS.ASSINATURA_ATIVA, String(serverData?.assinaturaAtiva !== false));
    writeJsonStorage(AUTH_KEYS.RECURSOS, recursos);

    writeStorage(AUTH_KEYS.PLANO_AGENDADO, nextPlanoAgendado);
    writeStorage(AUTH_KEYS.VALOR_PLANO_AGENDADO, nextValorPlanoAgendado);
    writeStorage(AUTH_KEYS.DOWNGRADE_AGENDADO, String(nextDowngradeAgendado));
    writeStorage(AUTH_KEYS.DOWNGRADE_EFETIVO_EM, nextDowngradeEfetivoEm);
    writeStorage(AUTH_KEYS.DOWNGRADE_SOLICITADO_EM, serverData?.downgradeSolicitadoEm || "");
    writeStorage(AUTH_KEYS.DOWNGRADE_SCHEDULE_ID, serverData?.downgradeScheduleId || "");

    setPlanId(effectivePlanId);
    setPlanName(effectiveMeta.name);
    setPlanPrice(formatPrice(effectivePrice));
    setDowngradeAgendado(nextDowngradeAgendado);
    setPlanoAgendado(nextPlanoAgendado);
    setValorPlanoAgendado(nextValorPlanoAgendado);
    setDowngradeEfetivoEm(nextDowngradeEfetivoEm);
  }

  async function confirmChangePlan() {
    const effectiveUserId = userId || readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!effectiveUserId) {
      setFeedback({
        type: "error",
        message: "ID do usuário não encontrado. Faça login novamente.",
      });
      setDialog(null);
      return;
    }

    setBusy(true);
    setFeedback({ type: "", message: "" });

    try {
      const response = await fetch(
        `${API_BASE}/stripeUpdatePlan/${encodeURIComponent(effectiveUserId)}`,
        {
          method: "PUT",
          headers: buildAuthHeaders(),
          body: JSON.stringify({
            priceId: nextPlan.newPriceId,
            plano: nextPlan.newPlanName,
            valorPlano: nextPlan.newPriceOnlyNumber,
          }),
        }
      );

      if (response.status === 401 || response.status === 403) {
        navigate("/login", { replace: true });
        return;
      }

      if (!response.ok) {
        const message = await readApiError(response);

        setFeedback({
          type: "error",
          message:
            message ||
            "Não foi possível atualizar o plano. Verifique se o usuário possui assinatura vinculada no Stripe.",
        });

        setDialog(null);
        return;
      }

      const payload = await response.json().catch(() => null);
      const serverData = payload?.data || payload || {};

      applyServerPlan(serverData);

      setFeedback({
        type: "success",
        message: payload?.message || "Solicitação processada com sucesso.",
      });

      setDialog(null);
    } catch (error) {
      console.error("Erro ao atualizar plano:", error);

      setFeedback({
        type: "error",
        message: "Erro de rede ao atualizar plano.",
      });

      setDialog(null);
    } finally {
      setBusy(false);
    }
  }

  async function confirmCancelPlan() {
    const effectiveUserId = userId || readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!effectiveUserId) {
      setFeedback({
        type: "error",
        message: "ID do usuário não encontrado. Faça login novamente.",
      });
      setDialog(null);
      return;
    }

    setBusy(true);
    setFeedback({ type: "", message: "" });

    try {
      const response = await fetch(
        `${API_BASE}/stripeCancelPlan/${encodeURIComponent(effectiveUserId)}`,
        {
          method: "PUT",
          headers: buildAuthHeaders(),
        }
      );

      if (response.status === 401 || response.status === 403) {
        navigate("/login", { replace: true });
        return;
      }

      if (!response.ok) {
        const message = await readApiError(response);

        setFeedback({
          type: "error",
          message: message || "Erro ao cancelar plano.",
        });

        setDialog(null);
        return;
      }

      clearAuthStorage();
      setDialog(null);
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Erro ao cancelar plano:", error);

      setFeedback({
        type: "error",
        message: "Erro de rede ao comunicar com o servidor.",
      });

      setDialog(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dash-shell dash-shell--dark pm-page">
      <div className="dash-orbs pm-bg" aria-hidden="true">
        <span className="dash-orb dash-orb--a" />
        <span className="dash-orb dash-orb--b" />
        <span className="dash-orb dash-orb--c" />
        <span className="dash-orb dash-orb--d" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="dash-topbar dash-topbar--dark pm-topbar">
        <button
          className="dash-btn-ghost dash-iconbtn pm-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} />
        </button>

        <div className="dash-brand pm-brand">
          <img src={Logo} alt="PsyAssist" className="dash-brand__logo pm-brand__logo" />
          <div className="dash-brand__txt pm-brand__text">
            <div className="dash-brand__title">Gestão do Plano</div>
            <div className="dash-brand__sub">Assinatura • Cobrança • {nomeUsuario}</div>
          </div>
        </div>

        <div className="pm-top-actions">
          <button className="pm-topbtn pm-topbtn--ghost" type="button" onClick={() => navigate("/settings")}>
            <ArrowLeft size={16} />
            Configurações
          </button>

          <button className="pm-topbtn pm-topbtn--primary" type="button" onClick={() => setDialog("change")} disabled={busy}>
            <Sparkles size={16} />
            Alterar
          </button>
        </div>
      </header>

      <main className="dash-main dash-main--dark pm-main">
        <div className="pm-wrap">
          <section className="pm-hero">
            <div className="pm-hero__text">
              <span className="pm-kicker"><CreditCard size={14} /> Assinatura e cobrança</span>
              <h1>Plano</h1>
              <p>
                {firstName ? `${firstName}, ` : ""}
                acompanhe seu plano atual, compare as opções e gerencie sua assinatura.
              </p>
            </div>

            <aside className={`pm-status pm-status--${currentPlan.tone}`}>
              <div className="pm-status__icon" aria-hidden="true">
                <CurrentPlanIcon size={22} />
              </div>

              <div>
                <small>Plano atual</small>
                <strong>{planName || currentPlan.name}</strong>
                <span>{planPrice || formatPrice(currentPlan.price)}</span>
              </div>
            </aside>
          </section>

          {downgradeAgendado ? (
            <div className="pm-feedback pm-feedback--info">
              Downgrade para <b>{planoAgendado || "PsyAssist Essencial"}</b> agendado
              {downgradeEfetivoEm ? ` para ${formatDateBR(downgradeEfetivoEm)}` : ""}.
              Até essa data, os recursos Premium continuam liberados.
            </div>
          ) : null}

          {feedback.message ? (
            <div className={`pm-feedback pm-feedback--${feedback.type || "info"}`}>
              {feedback.message}
            </div>
          ) : null}

          <section className="pm-card pm-card--current">
            <div className="pm-card__head">
              <div>
                <span>Plano atual</span>
                <h2>{planName || currentPlan.name}</h2>
                <p>{currentPlan.description}</p>
              </div>

              <div className="pm-price">
                <small>Mensalidade</small>
                <strong>{planPrice || formatPrice(currentPlan.price)}</strong>
              </div>
            </div>

            <PlanFeatureList features={currentPlan.features} />

            {downgradeAgendado ? (
              <div className="pm-note">
                Próximo plano: {planoAgendado || "PsyAssist Essencial"}
                {valorPlanoAgendado ? ` • R$ ${valorPlanoAgendado}/MÊS` : ""}
                {downgradeEfetivoEm ? ` • efetivo em ${formatDateBR(downgradeEfetivoEm)}` : ""}.
              </div>
            ) : null}

            <div className="pm-actions">
              <button
                className="pm-btn pm-btn--primary"
                type="button"
                onClick={() => setDialog("change")}
                disabled={busy}
              >
                {actionLabel}
              </button>

              <button
                className="pm-btn pm-btn--danger-soft"
                type="button"
                onClick={() => setDialog("cancel")}
                disabled={busy}
              >
                Cancelar Plano
              </button>
            </div>

            <div className="pm-note">
              Upgrade para Premium só libera recursos após confirmação da Stripe.
              Downgrade para Essencial é aplicado somente no fim do ciclo já pago.
            </div>
          </section>

          <section className="pm-card">
            <div className="pm-card__head pm-card__head--simple">
              <div>
                <span>Comparativo</span>
                <h2>Opções disponíveis</h2>
                <p>Veja seu plano atual e a alternativa disponível.</p>
              </div>
            </div>

            <div className="pm-options">
              <PlanOption
                plan={PLAN_META.essencial}
                active={planId === "essencial"}
                next={!downgradeAgendado && nextPlan.newPlanId === "essencial"}
              />

              <PlanOption
                plan={PLAN_META.premium}
                active={planId === "premium"}
                next={downgradeAgendado || nextPlan.newPlanId === "premium"}
              />
            </div>
          </section>

          <section className="pm-bottom">
            <button className="pm-btn pm-btn--ghost-dark" type="button" onClick={() => navigate("/settings")} disabled={busy}>
              <ArrowLeft size={16} />
              Configurações
            </button>

            <button className="pm-btn pm-btn--primary" type="button" onClick={() => setDialog("change")} disabled={busy}>
              {actionLabel}
            </button>
          </section>
        </div>
      </main>

      {dialog === "change" ? (
        <Modal onClose={closeDialog}>
          <div className="pm-dialog__icon pm-dialog__icon--primary" aria-hidden="true">
            <NextPlanIcon size={24} />
          </div>

          <div className="pm-modal__title">
            {downgradeAgendado ? "Manter Premium" : "Alterar Plano"}
          </div>

          <div className="pm-modal__desc">
            {downgradeAgendado ? (
              <>
                Você possui downgrade para o Essencial agendado.
                <br />
                <br />
                Ao confirmar, o downgrade será cancelado e seu plano Premium será mantido.
              </>
            ) : isPremium ? (
              <>
                Você está prestes a agendar a mudança de <b>{planName}</b> para{" "}
                <b>{nextPlan.newPlanName}</b>.
                <br />
                <br />
                Essa alteração será aplicada somente no fim do ciclo pago.
                Até lá, os recursos Premium continuam liberados.
              </>
            ) : (
              <>
                Você está prestes a alterar seu plano de <b>{planName}</b> para{" "}
                <b>{nextPlan.newPlanName}</b>.
                <br />
                <br />
                Novo valor: <b>{nextPlan.newPlanPrice}</b>
                <br />
                <br />
                Os recursos Premium só serão liberados após confirmação da Stripe.
              </>
            )}
          </div>

          <div className="pm-modal__actions">
            <button className="pm-mbtn pm-mbtn--ghost" type="button" onClick={closeDialog} disabled={busy}>
              Não
            </button>

            <button className="pm-mbtn pm-mbtn--primary" type="button" onClick={confirmChangePlan} disabled={busy}>
              {busy ? "Processando..." : "Confirmar"}
            </button>
          </div>
        </Modal>
      ) : null}

      {dialog === "cancel" ? (
        <Modal onClose={closeDialog}>
          <div className="pm-dialog__icon pm-dialog__icon--danger" aria-hidden="true">
            <AlertTriangle size={24} />
          </div>

          <div className="pm-modal__title">Cancelar Plano</div>

          <div className="pm-modal__desc">
            Tem certeza de que deseja cancelar seu plano?
            <br />
            <br />
            Isso encerrará seu acesso ao PsyAssist e você precisará reativar um plano para continuar utilizando o sistema.
          </div>

          <div className="pm-modal__actions">
            <button className="pm-mbtn pm-mbtn--ghost" type="button" onClick={closeDialog} disabled={busy}>
              Manter Plano
            </button>

            <button className="pm-mbtn pm-mbtn--danger" type="button" onClick={confirmCancelPlan} disabled={busy}>
              {busy ? "Processando..." : "Cancelar Plano"}
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
